<template>
  <v-container fluid>
    <v-row>
      <v-col cols="12">
        <v-tabs
          v-model="tab"
          bg-color="white"
          color="black"
          align-tabs="start"
        >

          <v-tab value="alarms">
            <v-icon class="mr-2" size="small">mdi-bell-ring</v-icon>
            <v-badge
              bordered
              color="error"
              v-if="activeAlarms.length > 0"
              :content="activeAlarms.length"
            >
              {{ $t("machines.tabHeaders.alarms") }}
            </v-badge>
            <span v-else>
              {{ $t("machines.tabHeaders.alarms") }}
            </span>
          </v-tab>
          <v-tab value="events" v-if="showEvents">
            <v-icon class="mr-2" size="small">mdi-bell-outline</v-icon>
            {{ $t("machines.tabHeaders.events") }}
          </v-tab>
        </v-tabs>
      </v-col>
    </v-row>

    <v-tabs-window v-model="tab">
      <!-- ALARMS -->
      <v-tabs-window-item value="alarms">
        <v-row>
          <v-col cols="12">
            <v-data-table
              :loading="isLoadingAlarms"
              :headers="alarmHeaders"
              :items="activeAlarms"
              :items-per-page="15"
              :search="search"
              item-value="itemNo"
              :sort-by="[{ key: 'severity', order: 'desc' }]"
            >
              <template v-slot:[`item.function`]="{ item }">
                <span>{{ formatFunction(item) }} </span>
              </template>
              <template v-slot:[`item.parameterId`]="{ item }">
                <span>{{ item.parameterId }} </span>
              </template>
              <template v-slot:[`item.severity`]="{ item }">
                <span>{{ severityLabel(item.severity) }}</span>
              </template>
              <template v-slot:[`item.engineSeconds`]="{ item }">
                <span>{{ $filters.engineHourFilter(item.engineSeconds) }}</span>
              </template>
              <template v-slot:[`item.alarm.engineSeconds`]="{ item }">
                <span>{{
                  $filters.engineHourFilter(item.alarm.engineSeconds)
                }}</span>
              </template>
              <template v-slot:[`item.receiveTime`]="{ item }">
                <span>{{ $filters.localeDateFilter(item.receiveTime) }}</span>
              </template>
              <template v-slot:[`item.alarm.receiveTime`]="{ item }">
                <span>{{
                  $filters.localeDateFilter(item.alarm.receiveTime)
                }}</span>
              </template>
              <template v-slot:[`item.extraData`]="{ item }">
                <span v-if="item.extraData" @click="showExtraInfo(item)">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon size="small" v-bind="props">mdi-information</v-icon>
                    </template>
                    <div
                      v-for="part in splitExtraData(item.extraData)"
                      :key="part.lineNo"
                    >
                      {{ part.paramName }}
                    </div>
                  </v-tooltip>
                </span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tabs-window-item>

      <!-- EVENTS -->
      <v-tabs-window-item value="events" v-if="showEvents">
        <v-row>
          <v-col cols="12" sm="12" lg="5">
            <v-text-field
              v-model="search"
              prepend-icon="mdi-magnify"
              :label="$t('common.filters.filter')"
              variant="underlined"
              hide-details
            ></v-text-field>
          </v-col>
          <v-col cols="12" sm="6" lg="3">
            <v-select
              :items="timeRangeItems"
              item-value="value"
              v-model="selectedTimeRange"
              :label="$t('common.filters.timeRange')"
              :disabled="isLoadingEventHistory"
              variant="underlined"
              :menu-props="{ scrollStrategy: 'none' }"
            ></v-select>
          </v-col>
          <v-col cols="11" sm="5" lg="3">
            <v-menu
              ref="datePickerMenu"
              v-model="datePickerMenu"
              :close-on-content-click="false"
              transition="scale-transition"
              scroll-strategy="none"
              max-width="290px"
              min-width="290px"
            >
              <template v-slot:activator="{ props }">
                <v-text-field
                  v-model="dateRangeText"
                  readonly
                  :placeholder="$t('common.placeholders.selectDates')"
                  :disabled="selectedTimeRange !== 'custom'"
                  variant="underlined"
                  clearable
                  @click:clear="onClearDates"
                  v-bind="props"
                ></v-text-field>
              </template>
              <v-date-picker v-model="dates" multiple="range">
                <template v-slot:actions>
                  <v-spacer></v-spacer>
                  <v-btn
                    variant="text"
                    color="primary"
                    @click="datePickerMenu = false"
                    >{{ $t("common.actions.cancel") }}</v-btn
                  >
                  <v-btn
                    variant="text"
                    color="primary"
                    :disabled="dates.length < 2"
                    @click="onSavePickedDate()"
                    >{{ $t("common.actions.ok") }}</v-btn
                  >
                </template>
              </v-date-picker>
            </v-menu>
          </v-col>
          <v-col cols="1" class="pl-0 d-flex justify-start align-center">
            <v-tooltip location="top">
              <template v-slot:activator="{ props }">
                <v-btn
                  v-bind="props"
                  icon
                  variant="text"
                  @click="fetchEventHistory"
                  :loading="refreshLoading"
                  :disabled="refreshLoading"
                >
                  <v-icon>mdi-cached</v-icon>
                  <template v-slot:loader>
                    <span class="refreshLoader">
                      <v-icon>mdi-cached</v-icon>
                    </span>
                  </template>
                </v-btn>
              </template>
              <span>{{ $t("common.actions.refresh") }}</span>
            </v-tooltip>
          </v-col>
        </v-row>
        <v-row>
          <v-col>
            <v-data-table
              :loading="isLoadingEventHistory"
              :headers="eventHeaders"
              :items="eventHistory"
              :items-per-page="15"
              :search="search"
              item-value="itemNo"
              :sort-by="[{ key: 'receiveTime', order: 'desc' }]"
            >
              <template v-slot:[`item.parameterId`]="{ item }">
                <span>{{ item.parameterId }} </span>
              </template>
              <template v-slot:[`item.active`]="{ item }">
                <span v-if="item.active">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon :color="severityColor(item.severity)" v-bind="props">{{
                        severityIcon(item.severity)
                      }}</v-icon>
                    </template>
                    <span>{{ $t("machines.dataTable.headers.active") }}</span>
                  </v-tooltip>
                </span>
              </template>

              <template v-slot:[`item.severity`]="{ item }">
                <span>{{ severityLabel(item.severity) }}</span>
              </template>
              <template v-slot:[`item.engineSeconds`]="{ item }">
                <span>{{ $filters.engineHourFilter(item.engineSeconds) }}</span>
              </template>
              <template v-slot:[`item.receiveTime`]="{ item }">
                <span>{{ $filters.localeDateFilter(item.receiveTime) }}</span>
              </template>
              <template v-slot:[`item.extraData`]="{ item }">
                <span v-if="item.extraData" @click="showExtraInfo(item)">
                  <v-tooltip location="top">
                    <template v-slot:activator="{ props }">
                      <v-icon size="small" v-bind="props">mdi-information</v-icon>
                    </template>
                    <div
                      v-for="part in splitExtraData(item.extraData)"
                      :key="part.lineNo"
                    >
                      {{ part.paramName }}
                    </div>
                  </v-tooltip>
                </span>
              </template>
            </v-data-table>
          </v-col>
        </v-row>
      </v-tabs-window-item>
    </v-tabs-window>

    <extra-info-dialog
      v-model="extraInfoDialog"
      :item="extraInfoItem"
      :machine="machine"
    />
  </v-container>
</template>
<script>
import Severity from "@/utils/severity"
import EventFilter from "@/filters/eventfilter"
import ExtraInfoDialog from "@/components/ExtraInfoDialog"

export default {
  name: "MachineAlarms",
  props: ["tenantUid", "serialNo"],
  components: { ExtraInfoDialog },
  data() {
    return {
      tab: "alarms",
      refreshLoading: true,
      search: "",
      dates: [],
      extraInfoDialog: false,
      extraInfoItem: null,

      datePickerMenu: false,

      selectedTimeRange: "today",
      dateRangeText: ""
    }
  },
  async created() {
    this.setDateRange()
    const langCode = this.$store.getters.dataLangCode

    if (this.$store.getters.eventMetadata.length === 0) {
      await this.$store.dispatch("loadEventMetadata", langCode)
    }

    if (this.$store.getters.measurementMetadata.length === 0) {
      await this.$store.dispatch("loadMeasurementMetadata", langCode)
    }
  },
  mounted() {
    this.fetchActiveAlarms()
  },
  computed: {
    machine() {
      return this.$store.getters.machineBySerialNo(this.serialNo)
    },
    isSmartPowerPlus() {
      return this.machine.properties?.tags?.includes("smart_power_plus")
    },
    showEvents() {
      return this.$store.getters.isService || this.$store.getters.isAdmin
    },
    alarmHeaders() {
      if (this.isSmartPowerPlus) {
        return [
          {
            title: this.$t("machines.dataTable.headers.function"),
            key: "function",
            // Not a field on the item, sort on the text shown in the cell
            value: item => this.formatFunction(item)
          },
          {
            title: this.$t("machines.dataTable.headers.latestTrigger"),
            key: "alarm.engineSeconds"
          },
          {
            title: this.$t("machines.dataTable.headers.count"),
            key: "alarm.count"
          },
          {
            title: this.$t("machines.dataTable.headers.date"),
            key: "alarm.receiveTime"
          }
        ]
      }

      return [
        {
          title: this.$t("machines.dataTable.headers.code"),
          key: "parameterId"
        },
        {
          title: this.$t("machines.dataTable.headers.description"),
          key: "description"
        },
        {
          title: this.$t("machines.dataTable.headers.severity"),
          key: "severity"
        },
        {
          title: this.$t("machines.dataTable.headers.engineHours"),
          key: "engineSeconds"
        },
        {
          title: this.$t("machines.dataTable.headers.date"),
          key: "receiveTime"
        },
        {
          title: this.$t("machines.dataTable.headers.extraInfo"),
          key: "extraData"
        }
      ]
    },
    eventHeaders() {
      return [
        {
          title: this.$t("machines.dataTable.headers.code"),
          key: "parameterId"
        },
        { title: this.$t("machines.dataTable.headers.active"), key: "active" },
        {
          title: this.$t("machines.dataTable.headers.description"),
          key: "description"
        },
        {
          title: this.$t("machines.dataTable.headers.severity"),
          key: "severity"
        },
        {
          title: this.$t("machines.dataTable.headers.engineHours"),
          key: "engineSeconds"
        },
        {
          title: this.$t("machines.dataTable.headers.date"),
          key: "receiveTime"
        },
        {
          title: this.$t("machines.dataTable.headers.extraInfo"),
          key: "extraData"
        }
      ]
    },
    timeRangeItems() {
      return [
        { title: this.$t("common.enums.day.today"), value: "today" },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            7
          )}`,
          value: "7 days"
        },
        {
          title: `${this.$t("common.filters.last")} ${this.$t(
            "common.units.day",
            30
          )}`,
          value: "30 days"
        },
        { title: this.$t("common.filters.customRange"), value: "custom" }
      ]
    },
    activeAlarms() {
      if (this.isSmartPowerPlus) {
        let alarms = this.$store.getters.groupAlarms
        return alarms
      }

      let alarms = this.$store.getters.activeAlarms
      for (let ix = 0; ix < alarms.length; ix++) {
        alarms[ix].description = EventFilter(alarms[ix].parameterId)
      }
      return alarms
    },
    isLoadingAlarms() {
      return this.$store.getters.loadingAlarms
    },
    eventHistory() {
      let events = this.$store.getters.eventHistory
      for (let ix = 0; ix < events.length; ix++) {
        events[ix].description = EventFilter(events[ix].parameterId)
      }
      return events
    },
    isLoadingEventHistory() {
      return this.$store.getters.loadingEventHistory
    }
  },
  methods: {
    showExtraInfo(item) {
      this.extraInfoItem = item
      this.extraInfoDialog = true
    },
    setDateRange() {
      let startDate = new Date()
      let offset = 0

      if (this.selectedTimeRange === "today") {
        offset = 0
      } else if (this.selectedTimeRange === "7 days") {
        offset = 6 // Becomes 7 since today will be included
      } else if (this.selectedTimeRange === "30 days") {
        offset = 29 // Becomes 30 since today will be included
      }

      startDate.setDate(startDate.getDate() - offset)
      this.dates = [
        startDate.toISOString().substring(0, 10),
        new Date().toISOString().substring(0, 10)
      ]
    },
    fetchActiveAlarms() {
      if (this.isSmartPowerPlus) {
        this.$store.dispatch("fetchGroupAlarms", {
          tenantUid: this.tenantUid,
          serialNo: this.serialNo
        })
      } else {
        this.$store.dispatch("fetchActiveAlarms", {
          tenantUid: this.tenantUid,
          serialNo: this.serialNo
        })
      }
    },
    async fetchEventHistory() {
      this.refreshLoading = true
      try {
        await this.$store.dispatch("fetchEventHistory", {
          tenantUid: this.tenantUid,
          serialNo: this.serialNo,
          start: this.formatIsoDate(this.dates[0]),
          end: this.formatIsoDate(this.dates[this.dates.length - 1])
        })
      } catch (error) {
        // Error already handled
      } finally {
        this.refreshLoading = false
      }
    },
    severityLabel(severity) {
      return Severity.label(severity)
    },
    severityIcon(severity) {
      return Severity.eventIcon(severity)
    },
    severityColor(severity) {
      return Severity.eventColor(severity)
    },
    splitExtraData(extraInfo) {
      var result = []

      // Maximum number of extras to display
      const MAX_NUM_EXTRA = 5

      const parts = extraInfo.split("|")
      let ix = 0
      for (ix = 0; ix < Math.min(parts.length, MAX_NUM_EXTRA); ix++) {
        // Skip engine temp if it's a Diesel engine.
        if (!(this.machine.hasDieselEngine && parts[ix].startsWith("M2"))) {
          result.push({ lineNo: ix, paramName: parts[ix] })
        }
      }
      if (parts.length > MAX_NUM_EXTRA) {
        result.push({
          lineNo: ix,
          paramName: `...${this.$t("machines.alarms.moreExists")}`
        })
      }
      return result
    },
    formatIsoDate(date) {
      // Dates set from the presets are already YYYY-MM-DD strings. The date
      // picker gives Date objects.
      if (typeof date === "string") {
        return date.substring(0, 10)
      }
      const month = String(date.getMonth() + 1).padStart(2, "0")
      const day = String(date.getDate()).padStart(2, "0")
      return `${date.getFullYear()}-${month}-${day}`
    },
    onClearDates() {
      // Clearing the custom range goes back to the default range ("today").
      // The selectedTimeRange watcher sets the dates and reloads the events.
      this.datePickerMenu = false
      this.selectedTimeRange = "today"
    },
    onSavePickedDate() {
      this.datePickerMenu = false
      // A range selection holds every date between start and end (sorted),
      // keep only the first and the last one.
      this.dates = [
        this.formatIsoDate(this.dates[0]),
        this.formatIsoDate(this.dates[this.dates.length - 1])
      ]
      this.fetchEventHistory()
    },
    formatFunction(item) {
      let fn = `${item.metadata.title} / ${item.metadata.text1} ${item.metadata.text2} ${item.metadata.text3}`
      if (item.showSplashText) {
        fn = `${fn}. ${item.metadata.splashText}`
      }
      return fn
    }
  },
  watch: {
    tab(newTab) {
      if (newTab === "events") {
        this.fetchEventHistory()
      } else {
        this.fetchActiveAlarms()
      }
    },
    selectedTimeRange(newRange) {
      if (newRange !== "custom") {
        this.setDateRange()
        this.fetchEventHistory()
      } else {
        // Display date picker. OK callback will trigger fetch
        this.datePickerMenu = true
      }
    },
    dates(newDates) {
      if (!newDates || newDates.length < 2) {
        this.dateRangeText = ""
        return
      }
      const start = this.formatIsoDate(newDates[0])
      const end = this.formatIsoDate(newDates[newDates.length - 1])
      this.dateRangeText = `${start} ~ ${end}`
    }
  }
}
</script>
<style></style>
